import { Softphone, useSoftphone } from "@dialstack/sdk-react/softphone";
import {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import Draggable, {
  DraggableBounds,
  DraggableData,
  DraggableEvent
} from "react-draggable";

import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import DialpadIcon from "@mui/icons-material/Dialpad";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import PhonePausedIcon from "@mui/icons-material/PhonePaused";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Fab from "@mui/material/Fab";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { useAppDispatch } from "app/hooks";
import { deviceCallDismissed } from "features/calls/dialstack/device-call.slice";
import useVoiceSeatNoticeAudience from "features/calls/voice-seat/use-voice-seat-notice-audience.hook";
import VoiceSeatNotice from "features/calls/voice-seat/voice-seat-notice.component";
import {
  VOICE_SEAT_NOTICE_COPY,
  noVoiceSeatFabLabel
} from "features/calls/voice-seat/voice-seat-notice.copy";
import usePracticeHotDeskingEnabled from "hooks/use-practice-hot-desking-enabled.hook";

import CallerIdSelect from "./caller-id-select.component";
import DeskPhoneCallCard from "./desk-phone-call-card.component";
import DeskPhonePicker from "./desk-phone/desk-phone-picker.component";
import DeskPhoneWalkUpBanner from "./desk-phone/desk-phone-walk-up-banner.component";
import useDeskPhones from "./desk-phone/use-desk-phones.hook";
import DeviceCallWorkbench from "./device-call-workbench.component";
import DialDeviceSelect, {
  DIAL_DEVICE_ROW_ID
} from "./dial-device-select.component";
import {
  callCardsSx,
  connectingChipSx,
  e911FabBadgeSx,
  fabSx,
  headerSx,
  headerTitleSx,
  minimizeButtonSx,
  panelSx,
  providerDialPendingSx,
  softphoneSurfaceSx,
  standbyFabSx,
  standbyNoticeIconOnCallSx,
  standbyNoticeIconSx,
  standbyNoticeSx,
  standbyPopoverSx,
  widgetContainerSx
} from "./dialstack-softphone-widget.styles";
import E911LocationBanner from "./e911-location-banner.component";
import IncomingCallPatientCard from "./incoming-call-patient-card.component";
import OutgoingCallPatientCard from "./outgoing-call-patient-card.component";
import SoftphoneCallBar from "./softphone-call-bar.component";
import SoftphoneCallPop from "./softphone-call-pop.component";
import SoftphoneSearch from "./softphone-search.component";
import SoftphoneStatusButton from "./softphone-status-button.component";
import SoftphoneWorkbench from "./softphone-workbench.component";
import useDeviceCallUnknownCaller from "./use-device-call-unknown-caller.hook";
import useDeviceCall, { useDeviceCallTracker } from "./use-device-call.hook";
import { otherDialDevice } from "./use-dial-device-preference.hook";
import { useDialstackSoftphone } from "./use-dialstack-softphone.hook";
import useE911AddressDelete from "./use-e911-address-delete.hook";
import useInboundCallPops, { isRinging } from "./use-inbound-call-pops.hook";
import { useLivePrivacyCleanup } from "./use-live-call-privacy.hook";
import usePopRecovery from "./use-pop-recovery.hook";
import { useSoftphoneOnCallInAnotherTab } from "./use-softphone-tab-leader.hook";
import useSoftphoneUnknownCaller from "./use-softphone-unknown-caller.hook";

// Floating shell for the DialStack softphone. Idle, the SDK's <Softphone />
// supplies the dial pad. A call ringing in opens Suno's own pop instead
// (ENG-2226): the plum panel headed by Decline and Answer with the caller's
// card inside, which folds down to a 56px bar; every other live call shows as
// its bar. Once a call is up with nothing ringing, the same frame is the
// workbench — the call's card with its jumps and Take payment over Suno's own
// row of call controls — with the same fold to the bar. The SDK's own call
// screens are never rendered. This component decides where it all floats,
// when it is collapsed to a FAB, and — since the SDK's E911 prompt is hidden
// by `softphoneSurfaceSx` — renders the 911 location picker itself. Mounted
// once in the authenticated layout.
export default function DialstackSoftphoneWidget() {
  const { enabled, blockedByOtherTab, connecting, needsVoiceSeat } =
    useDialstackSoftphone();
  // No Voice seat (ENG-2315): there is no phone, only the way to get one.
  if (needsVoiceSeat) return <NoVoiceSeatFab />;
  // Another tab holds the softphone (tab-leader election): no SDK provider is
  // mounted here, so render the standby shell with the take-over affordance.
  if (blockedByOtherTab) return <StandbySoftphoneFab />;
  // useSoftphone() throws outside SoftphoneProvider, which is only mounted
  // once a user session exists — so the inner widget must stay unrendered
  // until then. While that session is still being minted (or retried through
  // a backend blip) the chip says so instead of leaving a blank corner.
  if (!enabled)
    return connecting ? (
      <SoftphoneConnectingChip label="Phone connecting…" />
    ) : null;
  return <ConnectedSoftphoneWidget />;
}

// What sits in the FAB's spot while there is no phone to open yet. Read out
// on arrival (`role="status"`) so a screen-reader user hears why the phone
// button is missing.
function SoftphoneConnectingChip({ label }: { label: string }) {
  return (
    <Box sx={widgetContainerSx}>
      <Chip
        role="status"
        icon={<CircularProgress aria-hidden color="inherit" size={14} />}
        label={label}
        sx={connectingChipSx}
      />
    </Box>
  );
}

// Shown instead of the phone when another tab is the softphone leader: a
// muted FAB that explains the state and can move the phone to this tab. The
// other tab's softphone disconnects the moment leadership is stolen, so the
// button is explicit about the consequence — and is withheld while that tab
// is on a call, because a call cannot move with the phone (ENG-2178).
function StandbySoftphoneFab() {
  const { takeOverPhone } = useDialstackSoftphone();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const otherTabOnCall = useSoftphoneOnCallInAnotherTab(open);
  return (
    <Box sx={widgetContainerSx}>
      <Tooltip placement="left" title="Phone is active in another tab">
        <Fab
          aria-label="Phone is active in another tab"
          color="primary"
          size="medium"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={[fabSx, standbyFabSx]}
        >
          <PhonePausedIcon />
        </Fab>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Box sx={standbyPopoverSx}>
          <Box sx={standbyNoticeSx}>
            <Box
              aria-hidden
              sx={[
                standbyNoticeIconSx,
                otherTabOnCall && standbyNoticeIconOnCallSx
              ]}
            >
              {otherTabOnCall ? (
                <PhoneInTalkIcon fontSize="small" />
              ) : (
                <PhonePausedIcon fontSize="small" />
              )}
            </Box>
            <Box>
              <Typography fontWeight="fontWeightBold" variant="body2">
                {otherTabOnCall
                  ? "On a call in another tab"
                  : "Phone is active in another tab"}
              </Typography>
              <Typography color="text.secondary" variant="body1">
                {otherTabOnCall
                  ? "You can move the phone here once the call ends."
                  : "Only one tab can run the phone at a time. Move it here to answer and place calls from this tab — the other tab will disconnect."}
              </Typography>
            </Box>
          </Box>
          {!otherTabOnCall && (
            <Button
              size="small"
              variant="contained"
              onClick={async () => {
                if (await takeOverPhone()) setAnchorEl(null);
              }}
            >
              Use phone in this tab
            </Button>
          )}
        </Box>
      </Popover>
    </Box>
  );
}

// The phone's corner for someone without a Voice seat (ENG-2315): the dialer
// button, dormant like the standby one, opening the notice on how to get Voice
// turned on. A click-to-call anywhere opens the same notice through the
// context's `openDialer`, so the popover follows `isOpen` rather than local
// state, and is a dialog named by the notice's title so a screen reader says
// why focus moved there.
function NoVoiceSeatFab() {
  const { isOpen, openDialer, closeDialer } = useDialstackSoftphone();
  const audience = useVoiceSeatNoticeAudience();
  const label = noVoiceSeatFabLabel(VOICE_SEAT_NOTICE_COPY[audience].title);
  const titleId = useId();
  // State, not a ref: the notice can be opened from a call icon on the same
  // render the FAB mounts, and the popover needs its anchor by then.
  const [fab, setFab] = useState<HTMLButtonElement | null>(null);
  return (
    <Box sx={widgetContainerSx}>
      <Tooltip placement="left" title={label}>
        <Fab
          ref={setFab}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-label={label}
          color="primary"
          size="medium"
          onClick={isOpen ? closeDialer : openDialer}
          sx={[fabSx, standbyFabSx]}
        >
          <LocalPhoneIcon />
        </Fab>
      </Tooltip>
      <Popover
        open={isOpen && Boolean(fab)}
        anchorEl={fab}
        onClose={closeDialer}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        PaperProps={{ role: "dialog", "aria-labelledby": titleId }}
      >
        <VoiceSeatNotice titleId={titleId} onAction={closeDialer} />
      </Popover>
    </Box>
  );
}

const VIEWPORT_MARGIN = 12;

const DIAL_KEY_PATTERN = /^[0-9*#+]$/;

// What the SDK's number field may hold once it has sanitized the input.
const NON_DIAL_CHARS = /[^0-9*#+]/g;

export const PROVIDER_DIAL_PENDING_LABEL =
  "Connecting your phone… the call is placed once it picks up.";

// The SDK's number field is React-controlled, so write through the prototype
// value setter (bypassing React's value tracker) and fire an input event —
// the SDK's own onChange then sanitizes and stores the new value.
function setDialInputValue(input: HTMLInputElement, value: string) {
  const setter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    "value"
  )?.set;
  if (!setter) return;
  setter.call(input, value);
  input.dispatchEvent(new Event("input", { bubbles: true }));
}

// The widget's untransformed layout box plus the current drag translate.
// Built from offset* geometry rather than getBoundingClientRect so the
// panel's entrance scale animation can't skew the numbers mid-flight.
function getLayoutRect(node: HTMLElement, position: { x: number; y: number }) {
  const left = node.offsetLeft + position.x;
  const top = node.offsetTop + position.y;
  return {
    left,
    top,
    right: left + node.offsetWidth,
    bottom: top + node.offsetHeight,
    width: node.offsetWidth
  };
}

function ConnectedSoftphoneWidget() {
  const {
    isOpen,
    openDialer,
    closeDialer,
    popFolded,
    foldPop,
    unfoldPop,
    connecting,
    dialNumber,
    selectedCallerId,
    standingCallerId,
    providerDialPending,
    devices,
    dialDevice,
    deskPhoneDial,
    deskPhoneLegId
  } = useDialstackSoftphone();
  const {
    connection,
    calls,
    incomingCalls,
    activeCall,
    emergency,
    lastError,
    overlays
  } = useSoftphone();
  const { deletedIds, requestDelete } = useE911AddressDelete();
  const dispatch = useAppDispatch();

  // Ringing calls live in `incomingCalls`; answered ones move to `calls`.
  // Union by id so a call is counted once across that transition. A desk-phone
  // dial's own leg ringing here (ENG-2183) is left out: it is the user's call
  // going out, so it must not pop as an incoming one, and Decline on it could
  // cancel the call. The panel's desk-phone card stands in for it.
  const sdkCalls = [
    ...incomingCalls,
    ...calls.filter(
      (call) => !incomingCalls.some((incoming) => incoming.id === call.id)
    )
  ].filter((call) => call.id !== deskPhoneLegId);
  const hasCallActivity = sdkCalls.length > 0 || Boolean(activeCall);
  // The in-call "Private" state (ENG-2364) is kept per SDK call; forget it
  // once the call has left the SDK's list.
  useLivePrivacyCleanup(sdkCalls.map((call) => call.id));
  const inbound = useInboundCallPops().filter(
    (match) => match.callId !== deskPhoneLegId
  );
  // A live call with no pop is looked up by its provider id (ENG-2380), so
  // the unknown-caller actions get their backend call id either way.
  usePopRecovery(inbound);
  // The one call ringing in gets the unfolded pop; any other ringing call and
  // every call already up fold to their bars under it. With nothing ringing
  // the live call keeps the panel (cards above the SDK's in-call controls)
  // until it, too, is folded.
  const ringingPop = inbound.find((match) => isRinging(match.call)) ?? null;
  // Booleans and ids only: the match objects are rebuilt every render, and
  // the viewport clamp below keys on these.
  const ringingPopCallId = ringingPop?.callId ?? null;
  const showCallPop =
    isOpen && hasCallActivity && !popFolded && ringingPopCallId !== null;
  const showCallBars =
    isOpen && hasCallActivity && (popFolded || ringingPopCallId !== null);
  // Nothing ringing: the call being spoken on (else the first that is not
  // held, else the only one) gets the workbench and the rest are bars.
  const workbenchCall =
    isOpen && hasCallActivity && !popFolded && ringingPopCallId === null
      ? (activeCall ??
        sdkCalls.find((call) => call.state !== "held") ??
        sdkCalls[0] ??
        null)
      : null;
  const workbenchCallId = workbenchCall?.id ?? null;
  const showWorkbench = workbenchCallId !== null;
  const barCalls =
    showCallBars || showWorkbench
      ? sdkCalls.filter(
          (call) =>
            popFolded ||
            (call.id !== ringingPopCallId && call.id !== workbenchCallId)
        )
      : [];
  // A call the user took on their handset or desk phone (ENG-2562, ENG-2605):
  // the SDK has nothing for it, so without this the pop would drop to the
  // dial pad the moment they picked up. The tracker records how each browser
  // leg ended so a call answered HERE never shows as a handset call. The card
  // stacks above any call the browser does carry, as a second SDK call would.
  useDeviceCallTracker(inbound);
  const deviceCall = useDeviceCall();
  const deviceCallId = deviceCall?.callId ?? null;
  const deviceCallActive = deviceCall !== null && deviceCall.phase !== "ended";
  const showDeviceWorkbench = isOpen && deviceCallId !== null;
  const dismissDeviceCall = useCallback(() => {
    if (deviceCallId !== null) dispatch(deviceCallDismissed(deviceCallId));
  }, [deviceCallId, dispatch]);
  const { card: deviceCallerCard, dialogs: deviceCallerDialogs } =
    useDeviceCallUnknownCaller(showDeviceWorkbench ? deviceCall : null);
  const showPanel =
    isOpen &&
    !showCallPop &&
    !showCallBars &&
    !showWorkbench &&
    !showDeviceWorkbench;

  // The cards the idle panel may show. While the desk-phone leg rings it shows
  // none: that leg is not a caller, and the desk-phone card speaks for it.
  const deskPhoneLegRinging = Boolean(deskPhoneLegId);
  const panelCardCallIds = deskPhoneLegRinging ? [] : undefined;

  // Unknown-caller card (in the panel) and its dialogs (outside the panel —
  // the Paper's onMouseDown/onKeyDown would steal focus into the dial field,
  // and the dialogs must survive the panel collapsing or the call ending).
  const {
    card: unknownCallerCard,
    dialogs: unknownCallerDialogs,
    isRinging: unknownCallerIsRinging
  } = useSoftphoneUnknownCaller({
    cardCallIds:
      showCallPop && ringingPopCallId !== null
        ? [ringingPopCallId]
        : workbenchCallId !== null
          ? [workbenchCallId]
          : panelCardCallIds
  });
  // Outbound PSTN calls are rejected until a 911 location is confirmed, and a
  // minimised dialer would otherwise hide that completely.
  const needsE911Location =
    connection === "connected" && !emergency.loading && !emergency.bound;

  // "keypad" shows the SDK softphone; "patients" the Suno search tab. Any
  // call activity forces the SDK view — that's where answer/in-call controls
  // live — and hides the toggle until the line is idle again.
  const [activeTab, setActiveTab] = useState<"keypad" | "patients">("keypad");
  useEffect(() => {
    if (hasCallActivity) setActiveTab("keypad");
  }, [hasCallActivity]);
  const showPatientsTab = activeTab === "patients" && !hasCallActivity;

  // Hot desking (ENG-2293): the desk-phone picker replaces the dial pad while
  // open; any call activity sends the person back to the keypad, as the
  // Patients tab does. A log-in in flight carries on in the hook either way.
  const hotDeskingEnabled = usePracticeHotDeskingEnabled();
  const deskPhones = useDeskPhones({ enabled: hotDeskingEnabled });
  const [deskPhonesOpen, setDeskPhonesOpen] = useState(false);
  const showDeskPhones =
    hotDeskingEnabled && deskPhonesOpen && !hasCallActivity && !deskPhoneDial;
  const {
    dismiss: dismissDeskPhoneRequest,
    isError: deskPhonesFailed,
    refetch: refetchDeskPhones
  } = deskPhones;
  // Closed by any route — a ring, the Patients tab, the back arrow — a confirm
  // that was left open is dropped, so it cannot be acted on stale later.
  useEffect(() => {
    if (!showDeskPhones) dismissDeskPhoneRequest();
  }, [showDeskPhones, dismissDeskPhoneRequest]);
  const openDeskPhones = () => {
    if (deskPhonesFailed) refetchDeskPhones();
    setActiveTab("keypad");
    setDeskPhonesOpen(true);
  };
  // Focus goes back to the row the picker was opened from.
  const closeDeskPhones = useCallback(() => {
    setDeskPhonesOpen(false);
    window.requestAnimationFrame(() => {
      document
        .getElementById(DIAL_DEVICE_ROW_ID)
        ?.querySelector<HTMLElement>('[role="combobox"]')
        ?.focus();
    });
  }, []);

  const nodeRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const positionRef = useRef(position);
  positionRef.current = position;
  const [dragBounds, setDragBounds] = useState<DraggableBounds | undefined>(
    undefined
  );

  // Freeze this drag's bounds so neither the FAB nor the panel can be
  // dragged past the viewport edge. When the limits conflict (viewport
  // smaller than the panel), react-draggable applies `right`/`bottom`
  // first, so the left/top edge — and the drag handle — wins.
  const handleDragStart = useCallback(() => {
    const node = nodeRef.current;
    if (!node || !node.offsetWidth) return;
    const current = positionRef.current;
    const rect = getLayoutRect(node, current);
    setDragBounds({
      left: current.x + VIEWPORT_MARGIN - rect.left,
      right: current.x + window.innerWidth - VIEWPORT_MARGIN - rect.right,
      top: current.y + VIEWPORT_MARGIN - rect.top,
      bottom: current.y + window.innerHeight - VIEWPORT_MARGIN - rect.bottom
    });
  }, []);

  const handleDrag = useCallback(
    (_event: DraggableEvent, data: DraggableData) => {
      setPosition({ x: data.x, y: data.y });
    },
    []
  );

  // Opening swaps the ~48px FAB for a 320px panel that grows left and up
  // from the same right/bottom anchor, so a FAB parked near an edge would
  // put the panel off-screen. After every expand/collapse, nudge the widget
  // back inside the viewport (left/top edges win so the header stays
  // reachable).
  const hasUnknownCallerCard = Boolean(unknownCallerCard);
  const clampIntoViewport = useCallback(() => {
    const node = nodeRef.current;
    if (!node || !node.offsetWidth) return;
    setPosition((prev) => {
      const rect = getLayoutRect(node, prev);
      let dx = 0;
      let dy = 0;
      if (rect.right > window.innerWidth - VIEWPORT_MARGIN)
        dx = window.innerWidth - VIEWPORT_MARGIN - rect.right;
      if (rect.left + dx < VIEWPORT_MARGIN) dx = VIEWPORT_MARGIN - rect.left;
      if (rect.bottom > window.innerHeight - VIEWPORT_MARGIN)
        dy = window.innerHeight - VIEWPORT_MARGIN - rect.bottom;
      if (rect.top + dy < VIEWPORT_MARGIN) dy = VIEWPORT_MARGIN - rect.top;
      if (dx === 0 && dy === 0) return prev;
      return { x: prev.x + dx, y: prev.y + dy };
    });
  }, []);
  // calls.length / unknown-caller card / activeTab / transfer picker: the
  // call cards, a tab switch or the picker change the panel height, which
  // can push its bottom edge past the viewport.
  useLayoutEffect(clampIntoViewport, [
    clampIntoViewport,
    isOpen,
    popFolded,
    showCallPop,
    ringingPopCallId,
    workbenchCallId,
    barCalls.length,
    calls.length,
    hasUnknownCallerCard,
    activeTab,
    overlays.showTransfer,
    providerDialPending,
    deskPhoneDial?.phase,
    deviceCallId,
    deviceCallActive
  ]);
  // The height also changes with no state change here at all — a call card
  // swapping its skeleton for the patient, the SDK showing its call-waiting
  // banner — so follow the widget's real size as well.
  useEffect(() => {
    const node = nodeRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(clampIntoViewport);
    observer.observe(node);
    return () => observer.disconnect();
  }, [clampIntoViewport]);

  const findDialInput = useCallback(
    () =>
      nodeRef.current?.querySelector<HTMLInputElement>(
        "input.ds-destination"
      ) ?? null,
    []
  );

  // Type-to-dial: `autoFocusDestination` focuses the number field on open,
  // but pressing a dial key (or any panel chrome) would normally move focus
  // off it. When a dial character arrives while focus sits on some other
  // panel element, funnel it into the field. Real inputs (the field itself,
  // the transfer field) keep native typing, and in-call screens — where
  // typed digits must not silently queue into a hidden dial string — have
  // no `.ds-destination` and are left alone.
  const handlePanelKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const isDialKey = DIAL_KEY_PATTERN.test(event.key);
      if (!isDialKey && event.key !== "Backspace") return;
      const target = event.target as HTMLElement;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement
      )
        return;
      // Our 911 banner is ours, not the SDK's dial screen: its buttons and
      // address fields must keep their own keystrokes.
      if (target.closest("[data-suno-e911], [data-suno-transfer]")) return;
      const input = findDialInput();
      if (!input) return;
      event.preventDefault();
      input.focus();
      setDialInputValue(
        input,
        isDialKey ? input.value + event.key : input.value.slice(0, -1)
      );
    },
    [findDialInput]
  );

  // The SDK's own dial screen places calls itself, straight through its
  // WebRTC leg — which cannot carry a caller ID (ENG-1938) and cannot ring
  // the desk phone (ENG-2183). While the user has picked a number other than
  // the standing one, or has a desk phone at all (their standing choice, or
  // ⌥ on the Call button, may want it), take the dial over before the SDK
  // sees it (its handlers hang off React's root, so a native capture
  // listener on the panel runs first) and route it through `dialNumber`,
  // which knows when to place it via the platform.
  const interceptSdkDial =
    (selectedCallerId !== null && selectedCallerId !== standingCallerId) ||
    devices.length > 0;
  useEffect(() => {
    const node = nodeRef.current;
    if (!node || !interceptSdkDial) return undefined;
    const takeOver = (event: MouseEvent | KeyboardEvent) => {
      const input = findDialInput();
      if (!input) return;
      const destination = input.value.replace(NON_DIAL_CHARS, "");
      if (!destination) return;
      event.preventDefault();
      event.stopPropagation();
      setDialInputValue(input, "");
      dialNumber(destination, undefined, {
        callerId: selectedCallerId,
        ...(event.altKey && devices.length > 0
          ? { device: otherDialDevice(dialDevice) }
          : {})
      });
    };
    const handleClick = (event: MouseEvent) => {
      if ((event.target as HTMLElement).closest(".ds-call")) takeOver(event);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (event.key === "Enter" && target.matches("input.ds-destination"))
        takeOver(event);
    };
    node.addEventListener("click", handleClick, true);
    node.addEventListener("keydown", handleKeyDown, true);
    return () => {
      node.removeEventListener("click", handleClick, true);
      node.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [
    devices.length,
    dialDevice,
    dialNumber,
    findDialInput,
    interceptSdkDial,
    selectedCallerId
  ]);

  // Pointer presses on panel chrome (dial keys, whitespace, header) must not
  // pull focus out of the number field: block the default focus shift and,
  // on the dial screen, put focus in the field. Clicks still fire — only
  // the focus change is suppressed.
  const handlePanelMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      if (target.closest("input")) return;
      if (target.closest("[data-suno-e911], [data-suno-transfer]")) return;
      event.preventDefault();
      findDialInput()?.focus();
    },
    [findDialInput]
  );

  // The phone is dead. If a replacement session is on its way (ENG-1966), say
  // so where the FAB was; a terminal end (another tab took the phone, session
  // revoked) leaves the corner empty as before. While the SDK still holds a
  // call the panel stays: taking Hang up away mid-call leaves the patient on a
  // line nobody can end (ENG-2012).
  const phoneIsDead = connection === "error" || connection === "disconnected";
  if (phoneIsDead && !hasCallActivity)
    return connecting ? (
      <SoftphoneConnectingChip label="Phone reconnecting…" />
    ) : null;

  const banner = (
    <E911LocationBanner
      emergency={emergency}
      connection={connection}
      hasCallActivity={hasCallActivity}
      lastError={lastError}
      deletedIds={deletedIds}
      onDeleteAddress={requestDelete}
    />
  );

  return (
    <>
      {unknownCallerDialogs}
      {deviceCallerDialogs}
      {/* When expanded, only the header band drags — a stray press on a dial
          key must not scoot the panel. The collapsed FAB keeps the
          full-surface drag the widget has always had. The minimize button is
          excluded via `cancel`. */}
      <Draggable
        nodeRef={nodeRef}
        handle=".suno-phone-drag-handle"
        cancel=".suno-phone-no-drag"
        position={position}
        bounds={dragBounds}
        onStart={handleDragStart}
        onDrag={handleDrag}
      >
        <Box ref={nodeRef} sx={widgetContainerSx}>
          {showCallPop ||
          showCallBars ||
          showWorkbench ||
          showDeviceWorkbench ? (
            <Stack
              spacing={1.25}
              alignItems="flex-end"
              data-testid="softphone-call-stack"
            >
              {showCallPop && ringingPop && (
                <SoftphoneCallPop match={ringingPop} onFold={foldPop}>
                  <IncomingCallPatientCard callIds={[ringingPop.callId]} />
                  {unknownCallerCard}
                </SoftphoneCallPop>
              )}
              {/* A call ringing in leads the stack, as it does over a second
                  SDK call; the handset conversation sits under it. */}
              {showDeviceWorkbench && deviceCall && (
                <DeviceCallWorkbench
                  call={deviceCall}
                  banner={banner}
                  onDismiss={dismissDeviceCall}
                >
                  {deviceCallerCard}
                </DeviceCallWorkbench>
              )}
              {workbenchCall && (
                <SoftphoneWorkbench
                  call={workbenchCall}
                  match={
                    inbound.find(
                      (match) => match.callId === workbenchCall.id
                    ) ?? null
                  }
                  banner={banner}
                  onFold={foldPop}
                >
                  {unknownCallerCard}
                </SoftphoneWorkbench>
              )}
              {barCalls.map((call) => (
                <SoftphoneCallBar
                  key={call.id}
                  call={call}
                  match={
                    inbound.find((match) => match.callId === call.id) ?? null
                  }
                  onUnfold={popFolded ? unfoldPop : undefined}
                />
              ))}
            </Stack>
          ) : showPanel ? (
            <Paper
              elevation={0}
              sx={panelSx}
              onKeyDown={
                showPatientsTab || showDeskPhones
                  ? undefined
                  : handlePanelKeyDown
              }
              onMouseDown={
                showPatientsTab || showDeskPhones
                  ? undefined
                  : handlePanelMouseDown
              }
            >
              <Box className="suno-phone-drag-handle" sx={headerSx}>
                <SoftphoneStatusButton
                  connection={connection}
                  hasCallActivity={hasCallActivity}
                />
                <Typography component="span" sx={headerTitleSx}>
                  Make a Call
                </Typography>
                {!hasCallActivity && (
                  <IconButton
                    aria-label={
                      showPatientsTab
                        ? "Show keypad"
                        : "Search patients and contacts"
                    }
                    className="suno-phone-no-drag"
                    size="small"
                    onClick={() => {
                      setDeskPhonesOpen(false);
                      setActiveTab((current) =>
                        current === "keypad" ? "patients" : "keypad"
                      );
                    }}
                    sx={minimizeButtonSx}
                  >
                    {showPatientsTab ? (
                      <DialpadIcon fontSize="inherit" />
                    ) : (
                      <PersonSearchIcon fontSize="inherit" />
                    )}
                  </IconButton>
                )}
                <IconButton
                  aria-label="Minimize phone"
                  className="suno-phone-no-drag"
                  size="small"
                  onClick={closeDialer}
                  sx={minimizeButtonSx}
                >
                  <CloseFullscreenIcon fontSize="inherit" />
                </IconButton>
              </Box>
              {/* Panel level, not inside the `softphoneSurfaceSx` box: that box is
                hidden on the Patients tab, where dialing is blocked by the same
                rule and the SDK's own banner was never visible. */}
              {banner}
              {/* Only while the line is idle: mid-call there is nothing left
                to choose for, and the rows would push the in-call controls.
                A desk-phone call counts as activity for the device row too. */}
              {hotDeskingEnabled &&
                !hasCallActivity &&
                !deskPhoneDial &&
                !showDeskPhones && (
                  <DeskPhoneWalkUpBanner
                    controller={deskPhones}
                    onLogIn={(phone) => {
                      deskPhones.select(phone);
                      openDeskPhones();
                    }}
                  />
                )}
              {!hasCallActivity && !deskPhoneDial && !showDeskPhones && (
                <DialDeviceSelect
                  deskPhones={hotDeskingEnabled ? deskPhones : undefined}
                  onLogIn={hotDeskingEnabled ? openDeskPhones : undefined}
                  onLogOut={
                    hotDeskingEnabled
                      ? () => {
                          deskPhones.requestLogOut();
                          openDeskPhones();
                        }
                      : undefined
                  }
                />
              )}
              {!hasCallActivity && !deskPhoneDial && !showDeskPhones && (
                <CallerIdSelect />
              )}
              {providerDialPending && !hasCallActivity && (
                <Box role="status" sx={providerDialPendingSx}>
                  <CircularProgress aria-hidden color="inherit" size={14} />
                  {PROVIDER_DIAL_PENDING_LABEL}
                </Box>
              )}
              {/* Polite, so a call ringing in mid-call is read out without
                cutting across the conversation's own announcements. */}
              <Box aria-live="polite" sx={callCardsSx}>
                {/* A ringing call goes on top, whoever it is from. */}
                {unknownCallerIsRinging && unknownCallerCard}
                <IncomingCallPatientCard callIds={panelCardCallIds} />
                {!unknownCallerIsRinging && unknownCallerCard}
                <OutgoingCallPatientCard />
                <DeskPhoneCallCard />
              </Box>
              {showDeskPhones && (
                <DeskPhonePicker
                  controller={deskPhones}
                  onClose={closeDeskPhones}
                />
              )}
              {showPatientsTab && !showDeskPhones && (
                <SoftphoneSearch onDialed={() => setActiveTab("keypad")} />
              )}
              {/* The SDK view stays mounted (hidden) on the Patients tab so
                connection and call state never churn on a tab flip. It is
                hidden too while the desk-phone leg rings, when it would show
                its own incoming screen for that leg. */}
              <Box
                sx={[
                  softphoneSurfaceSx,
                  (showPatientsTab ||
                    showDeskPhones ||
                    deskPhoneLegRinging) && {
                    display: "none"
                  }
                ]}
              >
                <Softphone autoFocusDestination />
              </Box>
            </Paper>
          ) : (
            <Badge
              className="suno-phone-drag-handle"
              color="error"
              variant="dot"
              invisible={
                !hasCallActivity && !deviceCallActive && !needsE911Location
              }
              overlap="circular"
              // Call activity keeps the red dot; an unconfirmed 911 location is a
              // prompt, not a failure, so it gets the banner's amber.
              sx={
                !hasCallActivity && needsE911Location
                  ? e911FabBadgeSx
                  : undefined
              }
            >
              <Fab
                aria-label={
                  !hasCallActivity && needsE911Location
                    ? "Open phone — 911 location needed"
                    : "Open phone"
                }
                color="primary"
                size="medium"
                onClick={openDialer}
                sx={fabSx}
              >
                <LocalPhoneIcon />
              </Fab>
            </Badge>
          )}
        </Box>
      </Draggable>
    </>
  );
}
